import { Check, X } from 'lucide-react';
import { StoryStep } from './StoryStep';
import { isScoreLow, type LandingStatus } from './story-utils';
import type { LandingPageIssue } from '@/types/landing-page-analysis';
import type { AiInsight } from '@/types/sem';

interface LandingStepProps {
  pageH1: string;
  scores: {
    messageMatch: number;
    conversionOptimization: number;
    visualQuality: number;
    layoutScore: number;
  };
  issues: LandingPageIssue[];
  status: LandingStatus;
  insight?: AiInsight;
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="story-sc">
      <span className="story-sc-nm">{label}</span>
      <div className="story-sc-bar">
        <div
          className={`story-sc-fill${isScoreLow(value) ? ' story-sc-fill-low' : ''}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="story-sc-n">{value}</span>
    </div>
  );
}

export function LandingStep({ pageH1, scores, issues, status, insight }: LandingStepProps) {
  const isBroken = status === 'broken';

  return (
    <StoryStep
      label="User Lands on Page"
      tag={
        <div className={`story-tg ${isBroken ? 'story-tg-warn' : 'story-tg-ok'}`}>
          {isBroken ? <X /> : <Check />}
          {isBroken ? 'Broken' : 'Aligned'}
        </div>
      }
      collapsedStats={
        <>
          <div className="story-acc-stat">
            <span className="label">Match</span>
            <span>{scores.messageMatch}</span>
          </div>
          <div className="story-acc-stat">
            <span className="label">Conv</span>
            <span>{scores.conversionOptimization}</span>
          </div>
        </>
      }
    >
      <div className={`story-quote${isBroken ? ' story-quote-warn' : ''}`}>
        "{pageH1}"
      </div>

      {issues.length > 0 && (
        <div className="story-issues">
          {issues.map((issue, i) => (
            <div className="story-iss" key={i}>
              <div className="story-iss-marker" />
              <span
                className="story-iss-t"
                dangerouslySetInnerHTML={{ __html: issue.description }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="story-scores">
        <ScoreBar label="Msg Match" value={scores.messageMatch} />
        <ScoreBar label="Conversion" value={scores.conversionOptimization} />
        <ScoreBar label="Visual" value={scores.visualQuality} />
        <ScoreBar label="Layout" value={scores.layoutScore} />
      </div>
      {insight?.landing_page_issues && insight.landing_page_issues.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--ed-rule)]">
          <p className="text-[10px] text-[var(--ed-t4)] uppercase tracking-wider mb-2">
            AI-Detected Issues
          </p>
          <div className="space-y-2">
            {insight.landing_page_issues.map((iss, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`shrink-0 px-1 py-0.5 rounded text-[10px] font-medium ${
                  iss.severity === 'critical'
                    ? 'bg-[var(--ed-red-bg)] text-[var(--ed-red)]'
                    : 'bg-[var(--ed-amber-bg)] text-[var(--ed-amber)]'
                }`}>
                  {iss.severity}
                </span>
                <div>
                  <p className="text-[var(--ed-t2)]">{iss.issue}</p>
                  <p className="text-[var(--ed-t3)] mt-0.5">{iss.fix}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {insight?.rewrite_recommendations && insight.rewrite_recommendations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] text-[var(--ed-t4)] uppercase tracking-wider">
            Rewrite Suggestions
          </p>
          {insight.rewrite_recommendations.map((rec, i) => (
            <div key={i} className="text-xs ed-inset p-2 rounded-lg">
              <span className="text-[var(--ed-t3)]">{rec.element}:</span>
              <span className="text-[var(--ed-red)] line-through ml-1">{rec.current}</span>
              <span className="text-[var(--ed-green)] ml-1">{rec.suggested}</span>
            </div>
          ))}
        </div>
      )}
    </StoryStep>
  );
}
